/**
 * Turning a bank export table (CSV, or an XLSX sheet read as strings) into statement rows.
 *
 * Austrian exports differ in every detail - semicolons or commas, "15.03.2026", "-1.234,56",
 * sometimes two columns for debit and credit, sometimes card charges as positive numbers - so the
 * columns are guessed from the header and the user can correct the guess before importing.
 * Deciding which rows are already booked is reconcile.ts's job, not this file's.
 */
import {
  fxFromDescription,
  merchantFromDescription,
  toCents,
  type StatementRow,
} from "./statement";

export type ColumnKey =
  | "bookingDate"
  | "valueDate"
  | "amount"
  | "debit"
  | "credit"
  | "counterparty"
  | "description"
  | "reference"
  | "currency";

/** Column index per field; -1 means "not in this file". */
export type ColumnMapping = Partial<Record<ColumnKey, number>>;

export type ImportError = { row: number; reason: "date" | "amount" };

export type ImportResult = {
  rows: StatementRow[];
  errors: ImportError[];
};

/** Header names we recognise, lower-cased and without spaces or punctuation. */
const HEADER_HINTS: Array<[ColumnKey, string[]]> = [
  ["valueDate", ["valuta", "valutadatum", "wertstellung", "valuedate"]],
  [
    "bookingDate",
    [
      "buchungstag",
      "buchungsdatum",
      "belegdatum",
      "transaktionsdatum",
      "datum",
      "date",
      "bookingdate",
      "transactiondate",
      "postingdate",
    ],
  ],
  ["debit", ["soll", "belastung", "debit", "auszahlung", "ausgang"]],
  ["credit", ["haben", "gutschrift", "credit", "einzahlung", "eingang"]],
  ["amount", ["betrag", "umsatz", "amount", "betragineur", "value"]],
  [
    "counterparty",
    [
      "empfaenger",
      "empfanger",
      "auftraggeber",
      "zahlungsempfaenger",
      "beguenstigter",
      "haendler",
      "handler",
      "merchant",
      "payee",
      "counterparty",
      "name",
      "partnername",
    ],
  ],
  [
    "description",
    [
      // George puts the merchant into "Buchungs-Details" and leaves "Partnername" empty for cards.
      "buchungsdetails",
      "verwendungszweck",
      "buchungstext",
      "umsatztext",
      "beschreibung",
      "text",
      "description",
      "details",
      "narrative",
      "purpose",
    ],
  ],
  ["reference", ["referenz", "zahlungsreferenz", "mandatsreferenz", "reference", "endtoend"]],
  ["currency", ["waehrung", "wahrung", "currency", "ccy"]],
];

function normalizeHeader(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]/g, "");
}

/** Maps the header row onto our fields. First match per field wins, longest hint first. */
export function guessMapping(header: string[]): ColumnMapping {
  const normalized = header.map(normalizeHeader);
  const mapping: ColumnMapping = {};
  for (const [key, hints] of HEADER_HINTS) {
    const index = normalized.findIndex(
      (cell, position) =>
        cell.length > 0 &&
        hints.some((hint) => cell === hint || cell.startsWith(hint)) &&
        !Object.values(mapping).includes(position),
    );
    if (index !== -1) mapping[key] = index;
  }
  return mapping;
}

/** "-1.234,56", "1,234.56", "(120,00)" and "120,00-" all mean what you would expect. */
export function parseAmountCell(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  let text = raw.trim();
  if (text.length === 0) return undefined;

  let sign = 1;
  if (/^\(.*\)$/.test(text)) {
    sign = -1;
    text = text.slice(1, -1);
  }
  if (/-\s*$/.test(text)) {
    sign = -1;
    text = text.replace(/-\s*$/, "");
  }
  if (/^-/.test(text)) sign = -1;

  const cleaned = text.replace(/[^\d,.]/g, "");
  if (!/\d/.test(cleaned)) return undefined;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const normalized =
    lastComma > lastDot ? cleaned.replace(/\./g, "").replace(",", ".") : cleaned.replace(/,/g, "");

  const value = Number(normalized);
  if (!Number.isFinite(value)) return undefined;
  return Math.round(sign * Math.abs(value) * 100) / 100;
}

/** "15.03.2026", "2026-03-15", "15/03/2026" and "15.03.26" all become "2026-03-15". */
export function parseDateCell(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const text = raw.trim();

  const isoLike = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
  if (isoLike) return toIso(Number(isoLike[1]), Number(isoLike[2]), Number(isoLike[3]));

  const dayFirst = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})/.exec(text);
  if (dayFirst) {
    const year = Number(dayFirst[3]);
    return toIso(year < 100 ? 2000 + year : year, Number(dayFirst[2]), Number(dayFirst[1]));
  }
  return undefined;
}

function toIso(year: number, month: number, day: number): string | undefined {
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return undefined;
  return date.toISOString().slice(0, 10);
}

export type ImportOptions = {
  /** Some card providers export charges as positive numbers; flip them. */
  invertAmounts?: boolean;
  defaultCurrency?: string;
};

const cell = (row: string[], index: number | undefined) =>
  index === undefined || index < 0 ? undefined : row[index];

/** Converts data rows (without the header) into statement rows, reporting the rows it could not. */
export function rowsToStatementRows(
  rows: string[][],
  mapping: ColumnMapping,
  options: ImportOptions = {},
): ImportResult {
  const result: StatementRow[] = [];
  const errors: ImportError[] = [];

  rows.forEach((row, index) => {
    if (row.every((value) => value.trim().length === 0)) return;

    const date = parseDateCell(cell(row, mapping.bookingDate));
    if (!date) {
      errors.push({ row: index, reason: "date" });
      return;
    }

    let amount = parseAmountCell(cell(row, mapping.amount));
    if (amount === undefined) {
      // Separate debit / credit columns: one of them is filled.
      const debit = parseAmountCell(cell(row, mapping.debit));
      const credit = parseAmountCell(cell(row, mapping.credit));
      if (debit !== undefined && debit !== 0) amount = -Math.abs(debit);
      else if (credit !== undefined && credit !== 0) amount = Math.abs(credit);
    }
    if (amount === undefined) {
      errors.push({ row: index, reason: "amount" });
      return;
    }
    if (options.invertAmounts) amount = -amount;

    const counterparty = (cell(row, mapping.counterparty) ?? "").trim();
    const description = (cell(row, mapping.description) ?? "").trim();
    const currency = (cell(row, mapping.currency) ?? "").trim() || options.defaultCurrency || "EUR";

    result.push({
      family: "export",
      date,
      postingDate: date,
      valueDate: parseDateCell(cell(row, mapping.valueDate)),
      amountCents: toCents(amount),
      currency,
      merchant: counterparty || merchantFromDescription(description),
      counterparty,
      description,
      reference: (cell(row, mapping.reference) ?? "").trim(),
      location: "",
      ...fxFromDescription(description),
    });
  });

  return { rows: result, errors };
}
